import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardDescription, CardHeader, CardTitle } from './ui/card';
import { XCircle, Loader2, Lock, Eye, EyeOff, Copy, Check, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ActivateAccount() {
  const [token, setToken] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [acceptingPolicies, setAcceptingPolicies] = useState(false);
  const [policiesAcceptedAt, setPoliciesAcceptedAt] = useState(null);

  const verifyToken = useCallback(async (tokenValue) => {
    setVerifying(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/activate/verify/${tokenValue}`);
      const data = await response.json();
      setTokenData(data);

      if (data.politicas_aceptadas_at) {
        setPoliciesAccepted(true);
        setPoliciesAcceptedAt(data.politicas_aceptadas_at);
      }

      if (!data.valid) {
        if (data.expired) {
          toast.error('El enlace ha expirado');
        } else if (data.already_used) {
          toast.error('Esta cuenta ya ha sido activada');
        } else {
          toast.error('Enlace de activación no válido');
        }
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      toast.error('Error al verificar el enlace');
    } finally {
      setVerifying(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');

    if (!urlToken) {
      setVerifying(false);
      return;
    }

    setToken(urlToken);
    verifyToken(urlToken);
  }, [verifyToken]);

  const handleCopy = () => {
    if (tokenData?.decrypted_password) {
      navigator.clipboard.writeText(tokenData.decrypted_password);
      setCopied(true);
      toast.success('Contraseña copiada');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAcceptPolicies = async () => {
    if (policiesAccepted || acceptingPolicies) return;
    setAcceptingPolicies(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/activate/accept-policies/${token}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setPoliciesAccepted(true);
        setPoliciesAcceptedAt(data.accepted_at);
        toast.success('Aceptación de políticas registrada');
      }
    } catch {
      toast.error('No se pudo registrar la aceptación. Inténtalo de nuevo.');
    } finally {
      setAcceptingPolicies(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%)'
      }}>
        <Card style={{ maxWidth: '480px', width: '100%', margin: '20px' }}>
          <CardHeader style={{ textAlign: 'center', paddingBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <Loader2 size={48} style={{ color: '#1a5c2e', animation: 'spin 1s linear infinite' }} />
            </div>
            <CardTitle style={{ fontSize: '24px', color: '#1a1a1a' }}>Verificando enlace...</CardTitle>
            <CardDescription>Por favor espera un momento</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Invalid/Expired/Used token
  if (!tokenData || !tokenData.valid) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%)'
      }}>
        <Card style={{ maxWidth: '480px', width: '100%', margin: '20px' }}>
          <CardHeader style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <XCircle size={56} style={{ color: '#dc2626' }} />
            </div>
            <CardTitle style={{ fontSize: '24px', color: '#1a1a1a', marginBottom: '8px' }}>
              {tokenData?.already_used ? 'Cuenta ya activada' : tokenData?.expired ? 'Enlace expirado' : 'Enlace no válido'}
            </CardTitle>
            <CardDescription style={{ fontSize: '15px', lineHeight: '1.6' }}>
              {tokenData?.already_used
                ? 'Esta cuenta ya ha sido activada. Puedes iniciar sesión con tu contraseña.'
                : tokenData?.expired
                ? 'El enlace de activación ha expirado. Por favor, contacta con el equipo IT para obtener uno nuevo.'
                : 'El enlace de activación no es válido. Verifica que has copiado la URL completa.'}
            </CardDescription>
          </CardHeader>
          <div style={{ textAlign: 'center', paddingTop: '8px', paddingBottom: '24px' }}>
            <button
              onClick={() => window.location.href = '/'}
              style={{ background: '#1a5c2e', color: '#fff', fontWeight: 600, padding: '10px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}
            >
              Ir a inicio de sesión
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // Show password from AD (única vista — sin confirmación)
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a5c2e 0%, #2d8a4e 100%)',
      padding: '20px'
    }}>
      <Card style={{ maxWidth: '540px', width: '100%' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(26,92,46,0.1)',
            borderRadius: '50%',
            padding: '14px',
            marginBottom: '16px'
          }}>
            <Lock size={32} style={{ color: '#1a5c2e' }} />
          </div>
          <CardTitle style={{ fontSize: '26px', color: '#1a1a1a', marginBottom: '8px' }}>
            Tu contraseña corporativa
          </CardTitle>
          <CardDescription style={{ fontSize: '15px', lineHeight: '1.6' }}>
            Hola <strong>{tokenData.employee_name}</strong>, esta es la contraseña que el equipo IT asignó a tu cuenta <strong>{tokenData.employee_email}</strong>
          </CardDescription>
        </CardHeader>

        <div style={{ padding: '8px 24px 24px' }}>
          {/* Enrolamiento info banner */}
          <div
            data-testid="activate-enrolamiento-info"
            style={{
              background: '#f0faf3',
              border: '1px solid #cfe3d6',
              borderLeft: '4px solid #1a5c2e',
              borderRadius: '8px',
              padding: '14px 16px',
              marginBottom: '24px'
            }}
          >
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#1a5c2e', margin: '0 0 8px 0' }}>
              📱 Usa esta contraseña para enrolar tu dispositivo corporativo
            </p>
            <p style={{ fontSize: '12.5px', color: '#2d5c3d', margin: '0 0 8px 0', lineHeight: '1.6' }}>
              El cambio por la definitiva se realiza <strong>durante el enrolado</strong> de tu portátil o móvil Valoriza, a través del wizard de Microsoft Intune.
            </p>
            <p style={{ fontSize: '12px', color: '#4b7a5c', margin: 0, lineHeight: '1.55' }}>
              🛡️ Por <strong>acceso condicional</strong>, la contraseña solo puede cambiarse desde un dispositivo Valoriza enrolado. En un equipo personal no es posible.
            </p>
          </div>

          {/* Password Display */}
          <div style={{ marginBottom: '24px' }}>
            <Label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
              Contraseña de Active Directory
            </Label>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={tokenData.decrypted_password || ''}
                readOnly
                data-testid="activate-password-field"
                style={{
                  height: '48px',
                  fontSize: '16px',
                  fontFamily: 'monospace',
                  paddingRight: '88px',
                  background: '#f8f8f8',
                  fontWeight: 600,
                  color: '#1a5c2e'
                }}
              />
              <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="activate-toggle-visibility"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#888',
                    padding: '8px'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="activate-copy-button"
                  style={{
                    background: copied ? '#16a34a' : '#1a5c2e',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#fff',
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    transition: 'background 0.2s, color 0.2s'
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiada' : 'Copiar'}
                </button>
              </div>
            </div>
          </div>

          {/* Expiration info */}
          {tokenData.expires_at && (
            <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '16px', margin: '16px 0 0 0' }}>
              Este enlace expira el {new Date(tokenData.expires_at).toLocaleString('es-ES')}
            </p>
          )}

          {/* Policy acceptance */}
          <div style={{
            marginTop: '20px',
            background: policiesAccepted ? '#f0faf3' : '#fafafa',
            border: `1.5px solid ${policiesAccepted ? '#16a34a' : '#e5e7eb'}`,
            borderRadius: '10px',
            padding: '14px 16px',
            transition: 'border-color 0.3s, background 0.3s'
          }}>
            {policiesAccepted ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <ShieldCheck size={20} style={{ color: '#16a34a', flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#15803d', margin: '0 0 3px 0' }}>
                    Políticas de Seguridad IT aceptadas
                  </p>
                  <p style={{ fontSize: '11.5px', color: '#4b7a5c', margin: 0 }}>
                    Registrado el {new Date(policiesAcceptedAt).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </div>
              </div>
            ) : (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: acceptingPolicies ? 'wait' : 'pointer' }}>
                <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                  <input
                    type="checkbox"
                    data-testid="activate-policies-checkbox"
                    checked={policiesAccepted}
                    onChange={handleAcceptPolicies}
                    disabled={acceptingPolicies}
                    style={{ width: '17px', height: '17px', accentColor: '#1a5c2e', cursor: 'pointer' }}
                  />
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a', margin: '0 0 3px 0', lineHeight: 1.4 }}>
                    He leído y acepto las <strong style={{ color: '#1a5c2e' }}>Políticas de Seguridad IT de Valoriza</strong>
                  </p>
                  <p style={{ fontSize: '11.5px', color: '#888', margin: 0, lineHeight: 1.5 }}>
                    Medidas de Protección, Guía de Buenas Prácticas, ENS Alto, Política de Uso Aceptable
                  </p>
                </div>
                {acceptingPolicies && <Loader2 size={16} style={{ flexShrink: 0, color: '#1a5c2e', animation: 'spin 1s linear infinite', marginTop: '2px' }} />}
              </label>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
